import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Calculator, AlertTriangle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RISK_GRADE_COLORS = {
  'A': 'bg-green-100 text-green-700',
  'BBB': 'bg-blue-100 text-blue-700',
  'BB': 'bg-amber-100 text-amber-700',
  'B': 'bg-orange-100 text-orange-700',
  'CCC': 'bg-red-100 text-red-700',
};

const DECISION_COLORS = {
  'APPROVE': 'bg-emerald-500',
  'APPROVE_WITH_CONDITIONS': 'bg-amber-500',
  'REFER_TO_COMMITTEE': 'bg-orange-500',
  'DECLINE': 'bg-red-500',
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DemoSensitivityTab({ token, baseCase, onResultChange }) {
  const [fxChange, setFxChange] = useState(0);
  const [priceChange, setPriceChange] = useState(0);
  const [rateChange, setRateChange] = useState(0);
  const [revenueChange, setRevenueChange] = useState(0);
  const [sensitivityResult, setSensitivityResult] = useState(null);

  const calculateSensitivity = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        fx_change: fxChange,
        price_change: priceChange,
        rate_change: rateChange,
        revenue_change: revenueChange
      });
      
      const response = await fetch(`${API_URL}/api/demo/sensitivity/calculate?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSensitivityResult(data);
        if (onResultChange) {
          onResultChange(data);
        }
      }
    } catch (error) {
      console.error('Sensitivity calculation error:', error);
    }
  }, [fxChange, priceChange, rateChange, revenueChange, token, onResultChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateSensitivity();
    }, 300);
    return () => clearTimeout(timer);
  }, [calculateSensitivity]);

  const resetSliders = () => {
    setFxChange(0);
    setPriceChange(0);
    setRateChange(0);
    setRevenueChange(0);
  };

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-blue-600" />
          Interactive Sensitivity Analysis
        </CardTitle>
        <CardDescription>
          Adjust the sliders to see how changes in key variables affect the loan recommendation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Sliders */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <label className="text-sm font-medium">FX Rate Change (NGN/USD)</label>
                <span className={`text-sm font-bold ${fxChange > 0 ? 'text-red-600' : fxChange < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {fxChange > 0 ? '+' : ''}{fxChange}%
                </span>
              </div>
              <Slider value={[fxChange]} onValueChange={([v]) => setFxChange(v)} min={-20} max={40} step={5} className="py-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>NGN Strengthen</span>
                <span>NGN Depreciate</span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <label className="text-sm font-medium">Commodity Price Change</label>
                <span className={`text-sm font-bold ${priceChange > 0 ? 'text-emerald-600' : priceChange < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {priceChange > 0 ? '+' : ''}{priceChange}%
                </span>
              </div>
              <Slider value={[priceChange]} onValueChange={([v]) => setPriceChange(v)} min={-30} max={30} step={5} className="py-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>Price Drop</span>
                <span>Price Increase</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <label className="text-sm font-medium">Interest Rate Change</label>
                <span className={`text-sm font-bold ${rateChange > 0 ? 'text-red-600' : rateChange < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {rateChange > 0 ? '+' : ''}{rateChange}%
                </span>
              </div>
              <Slider value={[rateChange]} onValueChange={([v]) => setRateChange(v)} min={-2} max={4} step={0.5} className="py-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>Rate Cut</span>
                <span>Rate Hike</span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <label className="text-sm font-medium">Revenue Growth Change</label>
                <span className={`text-sm font-bold ${revenueChange > 0 ? 'text-emerald-600' : revenueChange < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {revenueChange > 0 ? '+' : ''}{revenueChange}%
                </span>
              </div>
              <Slider value={[revenueChange]} onValueChange={([v]) => setRevenueChange(v)} min={-15} max={15} step={2.5} className="py-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>Contraction</span>
                <span>Growth</span>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {sensitivityResult && (
          <div className="border-t border-slate-200 pt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">DSCR</p>
                <p className={`text-2xl font-bold ${sensitivityResult.outputs.dscr >= 1.5 ? 'text-emerald-600' : sensitivityResult.outputs.dscr >= 1.25 ? 'text-amber-600' : 'text-red-600'}`}>
                  {sensitivityResult.outputs.dscr}x
                </p>
                <p className="text-xs text-slate-400">Base: {baseCase.dscr}x</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Margin</p>
                <p className={`text-2xl font-bold ${sensitivityResult.outputs.margin >= 10 ? 'text-emerald-600' : sensitivityResult.outputs.margin >= 5 ? 'text-amber-600' : 'text-red-600'}`}>
                  {sensitivityResult.outputs.margin}%
                </p>
                <p className="text-xs text-slate-400">Base: {baseCase.margin}%</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Risk Score</p>
                <p className="text-2xl font-bold">{sensitivityResult.outputs.risk_score}</p>
                <Badge className={`text-xs ${RISK_GRADE_COLORS[sensitivityResult.outputs.risk_grade]}`}>
                  {sensitivityResult.outputs.risk_grade}
                </Badge>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">NPV Impact</p>
                <p className={`text-2xl font-bold ${sensitivityResult.outputs.npv >= baseCase.npv ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(sensitivityResult.outputs.npv)}
                </p>
                <p className="text-xs text-slate-400">Base: {formatCurrency(baseCase.npv)}</p>
              </div>
            </div>

            {/* Decision Box */}
            <div className={`p-6 rounded-lg ${DECISION_COLORS[sensitivityResult.recommendation.decision] || 'bg-slate-500'} text-white`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm opacity-90">Updated Recommendation</p>
                  <p className="text-3xl font-bold">{sensitivityResult.recommendation.decision?.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="bg-white/10 px-4 py-2 rounded-lg">
                    <p className="text-xs opacity-90">Amount</p>
                    <p className="text-lg font-bold">{formatCurrency(sensitivityResult.recommendation.recommended_amount)}</p>
                  </div>
                  <div className="bg-white/10 px-4 py-2 rounded-lg">
                    <p className="text-xs opacity-90">Spread</p>
                    <p className="text-lg font-bold">{sensitivityResult.recommendation.recommended_spread_bps} bps</p>
                  </div>
                  <div className="bg-white/10 px-4 py-2 rounded-lg">
                    <p className="text-xs opacity-90">All-in Rate</p>
                    <p className="text-lg font-bold">{sensitivityResult.recommendation.all_in_rate}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Warnings */}
            {sensitivityResult.warnings?.filter(Boolean).length > 0 && (
              <div className="mt-4 space-y-2">
                {sensitivityResult.warnings.filter(Boolean).map((warning, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Button variant="outline" onClick={resetSliders} className="w-full">
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset to Base Case
        </Button>
      </CardContent>
    </Card>
  );
}

export default DemoSensitivityTab;
